import React, { createContext, useEffect, useReducer } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// third-party
import { Chance } from 'chance';

// reducer - state management
import { LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import Loader from 'ui-component/Loader';
import axios from 'utils/axios';

// types
import { InitialLoginContextProps, JWTContextType } from 'types/auth';
import { UserProfile } from 'types/user-profile';

const chance = new Chance();

// constant
const initialState: InitialLoginContextProps = {
    isLoggedIn: false,
    isInitialized: false,
    user: null
};

// Users can save their views for the following datagrids
const dataGridStates = [
    "achievementsDataGridState",
    "activitiesDataGridState",
    "adminDeliveryDriversUserInstancesDataGridState",
    "adminDeliveryDriversUserDataGridState",
    "donorApplicationsDataGridState",
    "adminDonorsUserDataGridState",
    "adminAdminsUserDataGridState",
    "foodProductsDataGridState",
    "programsDataGridState",
    "recipientApplicationsDataGridState",
    "adminRecipientsUserDataGridState",
    "regionsDataGridState",
    "routeInstancesByDateDataGridState",
    "adminPendingRouteInstancesDataGridState",
    "adminMarketplaceRouteInstancesDataGridState",
    "adminRouteInstancesDataGridState",
    "adminRoutesDataGridState",
    "deliveryDriverRouteInstancesDataGridState",
    "donorRouteInstancesDataGridState",
    "recipientRouteInstancesDataGridState",
    "superuserOrganizationsDataGridState",
    "superuserSuperOrganizationsDataGridState",
    "superAdminOrganizationsDataGridState",
];

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //
const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState);

    const updateUser = (userUpdate: Partial<UserProfile>) => {
        if (!state.user) return;
        const updatedUser = { ...state.user, ...userUpdate };
        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: true,
                user: updatedUser
            }
        });
    };
    
    const navigate = useNavigate();
    const { search } = useLocation();

    useEffect(() => {
        const init = async () => {
            try {
                  const response = await axios.get('/berryAPI/me');
                  const user = response.data;
                  dispatch({
                      type: LOGIN,
                      payload: {
                          isLoggedIn: true,
                          user
                      }
                  });
            } catch (err) {
                console.error(err);
                dispatch({
                    type: LOGOUT
                });
            }
        };

        init();
    }, []);

    const login = async (email: string, password: string) => {
        const response = await axios.post('/berryAPI/login', { email, password });
        
        // Check if 2FA is required -> this if statement adds the 2FA check to the login flow
        if (response.data.requires_2fa) {
            // Store temporary session data for 2FA verification
            localStorage.setItem('temp_session', response.data.session);
            localStorage.setItem('temp_token', response.data.token);
            // Redirect to 2FA verification page
            navigate('/2fa' + search);
            return;
        }
        if (response.data.requires_2fa_setup) {
            // Store temporary session data for 2FA setup
            localStorage.setItem('temp_session', response.data.session);
            localStorage.setItem('temp_token', response.data.token);
            // Redirect to 2FA setup page
            navigate('/2fa-setup' + search);
            return;
        }

        const user = response.data.user;
        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: true,
                user
            }
        });

        const savedDatagridViews = JSON.parse(user.saved_datagrid_views);

        if (savedDatagridViews) //savedDatagridViews are null when user is first created, so need this check
        {
          dataGridStates.forEach((dataGridState : string) => {
              if (savedDatagridViews[dataGridState]) {
                  localStorage.setItem(dataGridState, savedDatagridViews[dataGridState]);
              }
          });
        }
    };
    
    const registerOrganization = async (
      organizationName: string,
      firstName: string,
      lastName: string,
      regionName: string,
      programName: string,
      phone: string,
      email: string,
      password: string,
      password_confirmation: string,
      createAsSuperOrganization: string,
  ) => {
      // todo: this flow need to be recode as it not verified
      const id = chance.bb_pin();
      const response = await axios
          .post('/berryAPI/registerOrganization', {
              organizationName,
              firstName,
              lastName,
              regionName,
              programName,
              phone,
              email,
              password,
              password_confirmation,
              createAsSuperOrganization,
          })
          .catch(function (error) {
              throw error;
          });
      if (response) { //this line causes it to redirect asap and skip all the code below. works but maybe not the best?
          return;
      }

      let users = response.data;

      if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
          const localUsers = window.localStorage.getItem('users');
          users = [
              ...JSON.parse(localUsers!),
              {
                  id,
                  email,
                  password,
                  name: `${firstName} ${lastName}`
              }
          ];
      }
      window.localStorage.setItem('users', JSON.stringify(users));
    };

    const register = async (
        role: string,
        firstName: string,
        lastName: string,
        businessName: string,
        address: string,
        city: string,
        province: string,
        postalCode: string,
        country: string,
        neighbourhood: string,
        regionID: string,
        programID: string,
        phone: string,
        donationList: string,
        donationAvailability: string,
        email: string,
        password: string,
        password_confirmation: string,
        how_did_you_hear_about_organization: string,
        name_of_group_or_company: string,
        parent_name_signed_waiver: string,
        foodCategories: number[],  
        timeAvailabilities: any[],
        answers: string,
        website: string
    ) => {
        // todo: this flow need to be recode as it not verified
        const id = chance.bb_pin();
        const response = await axios
            .post('/berryAPI/register', {
                role,
                firstName,
                lastName,
                businessName,
                address,
                city,
                province,
                postalCode,
                country,
                neighbourhood,
                regionID,
                programID,
                phone,
                donationList,
                donationAvailability,
                email,
                password,
                password_confirmation,
                how_did_you_hear_about_organization,
                name_of_group_or_company,
                parent_name_signed_waiver,
                foodCategories,
                timeAvailabilities,
                answers,
                website,
            })
            .catch(function (error) {
                throw error;
            });
        if (response) { //this line causes it to redirect asap and skip all the code below. works but maybe not the best?
            return;
        }

        let users = response.data;

        if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
            const localUsers = window.localStorage.getItem('users');
            users = [
                ...JSON.parse(localUsers!),
                {
                    id,
                    email,
                    password,
                    name: `${firstName} ${lastName}`
                }
            ];
        }
        window.localStorage.setItem('users', JSON.stringify(users));
    };

    const logout = async (loginRedirect: string|null) => {
        // Check for temp_token (used during 2FA setup)
        const tempToken = localStorage.getItem('temp_token');
        if (tempToken) {
            await axios.post('/berryAPI/logout', {}, {
                headers: {
                    Authorization: `Bearer ${tempToken}`
                }
            });
            localStorage.removeItem('temp_token');
            localStorage.removeItem('temp_session');
        } else {
            //regular logout
            await axios.post('/berryAPI/logout');    
        }
        if (loginRedirect && loginRedirect != '')
        {
          navigate('/login?redir=' + loginRedirect);
        }
        else
        {
          navigate('/login');
        } 
        dispatch({ type: LOGOUT });
    };

    const forgotPassword = async (email: string) => {
        await axios.post('/berryAPI/forgotPassword', { email });
    };

    const resetPassword = async (email: string, token: string, password: string, password_confirmation: string) => {
        await axios.post('/berryAPI/resetPassword', { email, token, password, password_confirmation });
    };

    const updateProfile = () => {};
    
    const deleteAccount = async (organization: string, email: string) => {
        await axios.post('/berryAPI/deleteAccount', { organization, email });
    };

    // Save Datagrid configurations on existing sessions
    const saveDatagridStates = async () => {
        if (state.isLoggedIn) {
            var userDataGridStates : any = {};
            dataGridStates.map((dataGridState : string) => {
                userDataGridStates[dataGridState] = localStorage.getItem(dataGridState);
            });

            await axios.post('/berryAPI/saveDatagridStates', { userDataGridStates: userDataGridStates });
        }
    };

    // Save Datagrid configurations on each render
    saveDatagridStates();

    const finishTwoFactorLogin = (token: string, session: string, user: any) => {
        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: true,
                user
            }
        });

        const savedDatagridViews = JSON.parse(user.saved_datagrid_views);

        if (savedDatagridViews) //savedDatagridViews are null when user is first created, so need this check
        {
          dataGridStates.forEach((dataGridState : string) => {
              if (savedDatagridViews[dataGridState]) {
                  localStorage.setItem(dataGridState, savedDatagridViews[dataGridState]);
              }
          });
        }
    };

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    return (
        <JWTContext.Provider value={{ ...state, login, logout, register, registerOrganization, forgotPassword, resetPassword, updateProfile, deleteAccount, finishTwoFactorLogin, updateUser }}>
            {children}
        </JWTContext.Provider>
    );
};

export default JWTContext;
